import { CiType } from '../../models/ci-type';
import { formatCurrency } from '@angular/common';
import { AnalysisTreeModel } from '../../models/analysis-tree.model';
import { Analysis } from '../../models/analysis.model';
import { TestQuotationLine } from '../../models/quotable-items/test-quotation-line.model';
import { PackageQuotationLine } from '../../models/quotable-items/package-quotation-line.model';
import { QuotationCommercialItem } from '../../models/quotable-items/quotation-commercial-item.model';
import { CustomerPackage } from '../../models/quotable-items/customer-package.model';
import { SubGroup } from '../../models/quotable-items/sub-group.model';
import { AnalysisType } from '../../models/analysis-type.enum';
import { QuotedItem } from '../../models/quotable-items/quoted-item.model';

export default class AnalysisHelper {
    static EntireSubGroupPattern = '^SG[|][A-Z0-9]*[|][A-Z0-9]*$';
    static currencyCode: string;
    static userLanguage: string;

    //#region Mapping Analysis

    static mapSGTest(
        quotationLines: TestQuotationLine[], tree: AnalysisTreeModel,
        selectedAnalysis: AnalysisTreeModel[], alreadySelectedAnalysis: Analysis[]) {
        quotationLines.filter(f => !f.hideItem).forEach(ql => {
            const node = this.getTestNode(ql, tree.data.id);
            const item = alreadySelectedAnalysis
                .filter(x => x.analysisType === AnalysisType.SubgroupWithTest && x.eurodatKey === ql.code).shift();
            if (item) {
                node.data.executionNumber = item.executionNumber;
                selectedAnalysis.push(node);
            }
            tree.children.push(node);
        });
    }

    static mapSGPackage(
        quotationLines: any[], tree: AnalysisTreeModel,
        selectedAnalysis: AnalysisTreeModel[], alreadySelectedAnalysis: Analysis[]) {
        quotationLines.filter(f => !f.doNotDisplay).forEach(ql => {
            const node = this.getPackageNode(ql, tree.data.id);
            const item = alreadySelectedAnalysis
                .filter(x => x.analysisType === AnalysisType.SubgroupWithPackage && x.eurodatKey === ql.code).shift();
            if (item) {
                node.data.executionNumber = item.executionNumber;
                selectedAnalysis.push(node);
            }
            tree.children.push(node);
        });
    }

    static mapTests(
        qlTest: TestQuotationLine[], tree: AnalysisTreeModel[],
        selectedAnalysis: AnalysisTreeModel[], alreadySelectedAnalysis: Analysis[]) {
        qlTest?.filter(f => !f.hideItem).forEach(t => {
            const node = this.getTestNode(t);
            const item = alreadySelectedAnalysis
                .filter(x => x.analysisType === AnalysisType.FreeQLTest && x.eurodatKey === t.code).shift();
            if (item) {
                node.data.executionNumber = item.executionNumber;
                selectedAnalysis.push(node);
            }
            tree.push(node);
        });
    }

    static mapPackages(
        qlPackage: PackageQuotationLine[], tree: AnalysisTreeModel[],
        selectedAnalysis: any, alreadySelectedAnalysis: Analysis[]) {
        qlPackage?.filter(f => !f.hideItem).forEach(p => {
            const node = this.getPackageNode(p);
            const item = alreadySelectedAnalysis
                .filter(x => x.analysisType === AnalysisType.FreeQLPackage && x.eurodatKey === p.code).shift();
            if (item) {
                node.data.executionNumber = item.executionNumber;
                selectedAnalysis.push(node);
            }
            tree.push(node);
        });
    }

    static getSubGroupItem(selectedNode: AnalysisTreeModel): Analysis {
        const sgCode = selectedNode.data.id.split('|')[2];
        const isCp = selectedNode.data.isCustomerPackage;
        return this.getAnalysis(selectedNode.data.eurodatKey,
            selectedNode.data.name, null, null, sgCode, isCp,
            selectedNode.data.priceValue, selectedNode.data.code, selectedNode.data.executionNumber, selectedNode.data.preparationTestCodes, selectedNode.data.sortIndex);
    }

    static getIndividualCiItems(selectedNode: any): Analysis {
        const analysisData = selectedNode.node.data;

        const ciId = analysisData.id.split('|');
        const ciType = ciId[0];
        const ciCode = ciId[1];
        const sgCode = ciId[3];
        const sgkey = ciId[4];
        const packageCode = ciType === CiType.Package ? ciCode : null;
        const testCode = ciType === CiType.Test ? ciCode : null;

        return this.getAnalysis(selectedNode.node.data.eurodatKey,
            selectedNode.node.data.name, testCode, packageCode, sgkey, false, selectedNode.node.data.priceValue,sgCode,
             selectedNode.node.data.executionNumber,
            selectedNode.node.data.preparationTestCodes, analysisData.sortIndex);

    }

    static mapToViewModel(
        quotationCommercialItem: QuotationCommercialItem,
        showPrice: boolean, selectedAnalysis: AnalysisTreeModel[],
        alreadySelectedAnalysis: Analysis[]): AnalysisTreeModel[] {
        const tempTreeNodes: AnalysisTreeModel[] = [];
        this.currencyCode = quotationCommercialItem?.currencyCode + ' ';
        // Customer Package
        quotationCommercialItem?.customerPackages?.filter(f => !f.hideItem).forEach(cp => {
            const node: AnalysisTreeModel = this.getCustomerPackageNode(cp);
            const item = alreadySelectedAnalysis
                .filter(x => x.analysisType === AnalysisType.CustomerPackage && x.eurodatKey === cp.code).shift();
            if (item) {
                node.data.executionNumber = item.executionNumber;
                selectedAnalysis.push(node);
            }
            tempTreeNodes.push(node);
        });
        // Sub Group
        quotationCommercialItem?.commercialItemSubGroups?.filter(f => !f.hideItem).forEach(sg => {
            const node: AnalysisTreeModel = this.getSubgroupNode(sg, showPrice);

            const subGroupItemsSelected = alreadySelectedAnalysis.filter(x => x.subGroupKey === sg.code);
            const lengthOfSubgroup = sg.packageQuotationLines.filter(x => !x.isStructuringQuotationLine).length
                + sg.testQuotationLines.filter(x => !x.isStructuringQuotationLine).length;
            node.data.isStructuringQuotationLine = sg.testQuotationLines.every(c => c.isStructuringQuotationLine)
                && sg.packageQuotationLines.every(c => c.isStructuringQuotationLine);
            if (subGroupItemsSelected && subGroupItemsSelected.length === lengthOfSubgroup) {
                node.data.isSelected = true;
                selectedAnalysis.push(node);
            } else {
                node.partialSelected = subGroupItemsSelected && subGroupItemsSelected.length > 0;
            }

            this.mapSGTest(sg.testQuotationLines, node, selectedAnalysis, alreadySelectedAnalysis);
            this.mapSGPackage(sg.packageQuotationLines, node, selectedAnalysis, alreadySelectedAnalysis);
            this.sortSGCommercialItems(node);
            tempTreeNodes.push(node);
        });
        // Test & Package
        this.mapTests(quotationCommercialItem?.freeQuotationLinesTest, tempTreeNodes, selectedAnalysis, alreadySelectedAnalysis);
        this.mapPackages(quotationCommercialItem?.freeQuotationLinesPackage, tempTreeNodes, selectedAnalysis, alreadySelectedAnalysis);

        const sortedNodes = this.sortCommercialItems(tempTreeNodes);
        return sortedNodes;
    }

    //#endregion Mapping Analysis

    //#region Analysis Node

    static formatCurrencyValue(value: number, currency: string): string {
        this.userLanguage = sessionStorage.getItem('LanguageCode') ? sessionStorage.getItem('LanguageCode')
        : navigator.language.split('-')[0]
        return formatCurrency(value, this.userLanguage, currency);
    }

    static getAnalysisFromNode(selectedNode: any): Analysis {
        return new RegExp(this.EntireSubGroupPattern).test(selectedNode.node.data.id)
            ? this.getSubGroupItem(selectedNode.node)
            : this.getIndividualCiItems(selectedNode);
    }

    static formatUniqueId(ciType: CiType, childCode: string, rootCode?: string) {
        return rootCode
            ? ciType + '|' + childCode + '|' + rootCode
            : ciType + '|' + childCode;
    }

    static getCustomerPackageNode(cp: CustomerPackage): AnalysisTreeModel {
        return {
            data: {
                id: this.formatUniqueId(CiType.Subgroup, cp.comlimsCode, cp.code),
                code: cp.comlimsCode,
                name: cp.name,
                price: this.formatCurrencyValue(cp.price, this.currencyCode),
                priceValue: cp.price,
                isCustomerPackage: true,
                sortIndex: cp.sortIndex,
                isSelected: cp.isSelected,
                eurodatKey: cp.code,
                isFavorite: cp.isFavorite,
                executionNumber : cp.executionNumber ?? 1
            }
        };
    }

    static getSubgroupNode(sg: SubGroup, showPrice: boolean): AnalysisTreeModel {
        let totalTestPrice = 0;
        let totalPackagePrice = 0;
        if (showPrice) {
            totalTestPrice = sg.testQuotationLines.reduce((prev: any, cur: any) => prev + cur.price, 0);
            totalPackagePrice = sg.packageQuotationLines.reduce((prev: any, cur: any) => prev + cur.price, 0);
        }

        const subgroupNode: AnalysisTreeModel = {
            data: {
                id: this.formatUniqueId(CiType.Subgroup, sg.comlimsCode, sg.code),
                code: sg.comlimsCode,
                name: sg.name,
                price: this.formatCurrencyValue((totalTestPrice + totalPackagePrice), this.currencyCode),
                priceValue: totalTestPrice + totalPackagePrice,
                isCustomerPackage: false,
                sortIndex: sg.sortIndex,
                isSelected: false,
                eurodatKey: sg.code,
                isFavorite: sg.isFavorite,
                isPartialFavorite: sg.isPartialFavorite,
                executionNumber : sg.executionNumber ?? 1
            },
            children: [],
            partialSelected: sg.isPartialSelected
        };
        return subgroupNode;
    }

    static getPackageNode(p: PackageQuotationLine, sgId?: string): AnalysisTreeModel {
        return {
            data: {
                id: this.formatUniqueId(CiType.Package, p.packageCode, sgId),
                code: p.packageCode,
                name: p.text,
                price: this.formatCurrencyValue(p.price, this.currencyCode),
                priceValue: p.price,
                sortIndex: p.sortIndex,
                eurodatKey: p.code,
                isSelected: p.isSelected,
                isFavorite: p.isFavorite,
                isStructuringQuotationLine: p.isStructuringQuotationLine,
                executionNumber : p.executionNumber ?? 1
            },
        };
    }

    static getTestNode(t: TestQuotationLine, sgId?: string): AnalysisTreeModel {
        return {
            data: {
                id: this.formatUniqueId(CiType.Test, t.testCode, sgId),
                code: t.testCode,
                name: t.text,
                price: this.formatCurrencyValue(t.price, this.currencyCode),
                priceValue: t.price,
                sortIndex: t.sortIndex,
                eurodatKey: t.code,
                isSelected: t.isSelected,
                isFavorite: t.isFavorite,
                preparationTestCodes: t.preparationTestCodes,
                isStructuringQuotationLine: t.isStructuringQuotationLine,
                executionNumber :  t.executionNumber ?? 1,
            },
        };
    }

    static sortCommercialItems(tempTreeNodes: AnalysisTreeModel[]) {
        return tempTreeNodes.sort((a, b) => {
            let result: number;
            // if sort index of two ci is same
            if (a.data.sortIndex === b.data.sortIndex) {
                const aType = a.data.id.split('|')[0];
                const bType = b.data.id.split('|')[0];
                // for same sort index of (SG / CP) & (T/P): consider (T/P) first
                // for same sort index of CP & SG : consider QuotationSubGroupCode
                // If (T/P) have the same sorting number, then sort by alphanumerical order of the CI code
                if (aType === CiType.Subgroup && (bType === CiType.Test || bType === CiType.Package)) {
                    result = 1;
                } else if (bType === CiType.Subgroup && (aType === CiType.Test || aType === CiType.Package)) {
                    result = -1;
                } else if ((aType === CiType.Subgroup && bType === CiType.Subgroup) ||
                    (aType === CiType.Test || aType === CiType.Package) && (bType === CiType.Test || bType === CiType.Package)) {
                    result = a.data.code > b.data.code ? 1 : -1;
                }
            } else {
                result = a.data.sortIndex - b.data.sortIndex;
            }
            return result;
        });
    }

    static sortSGCommercialItems(tree: AnalysisTreeModel) {
        return tree.children.sort((a, b) => {
            if (a.data.sortIndex === b.data.sortIndex) {
                return a.data.code > b.data.code ? 1 : -1;
            } else {
                return a.data.sortIndex - b.data.sortIndex;
            }
        });
    }

    static getAnalysis(
        eurodatKey: string, displayName: string, tCode?: string,
        pCode?: string, sgKey?: string,
        isCP = false, price?: number, subgroupCode?: string, executionNumber? : number,preparationTestCodes?: string[], sortIndex?: number)
        : Analysis {
        const analysisType = this.getAnalysisType(isCP, tCode, pCode, sgKey);
        return {
            eurodatKey,
            subGroupKey: sgKey,
            packageCode: pCode,
            testCode: tCode,
            isCustomerPackage: isCP,
            displayName,
            price,
            analysisType,
            subgroupCode,
            preparationTestCodes,
            isMandatory: false,
            sortIndex,
            executionNumber : executionNumber ?? 1

        };
    }

    private static getAnalysisType(isCustomerPackage: boolean, testCode: string, packageCode: string, subGroupKey: string): AnalysisType {
        if (subGroupKey) {
            if (isCustomerPackage) {
                return AnalysisType.CustomerPackage;
            } else if (testCode) {
                return AnalysisType.SubgroupWithTest;
            } else if (packageCode) {
                return AnalysisType.SubgroupWithPackage;
            } else {
                return AnalysisType.FullSubgroup;
            }
        }
        return testCode ? AnalysisType.FreeQLTest : AnalysisType.FreeQLPackage;
    }

    static getSubGroupQuotationLines(quotationCommercialItem: QuotationCommercialItem, subGroupKey: string): Analysis[] {
        const subGroup = quotationCommercialItem?.commercialItemSubGroups?.filter(x => x.code === subGroupKey).shift();
        const listAnalysis: Analysis[] = [];
        subGroup?.packageQuotationLines?.filter(x => !x.isStructuringQuotationLine).forEach((p: PackageQuotationLine) => {
            const analysis = this.getAnalysis(p.code, p.text, null, p.packageCode, subGroupKey, false,
                p.price, subGroup.comlimsCode,p.executionNumber, null, p.sortIndex);
            listAnalysis.push(analysis);
        });
        subGroup?.testQuotationLines?.filter(x => !x.isStructuringQuotationLine).forEach((t: TestQuotationLine) => {
            const analysis = this.getAnalysis(t.code, t.text, t.testCode, null, subGroupKey, false,
                t.price, subGroup.comlimsCode,t.executionNumber, t.preparationTestCodes, t.sortIndex);
            listAnalysis.push(analysis);
        });
        return listAnalysis;
    }

    static unselectItemFromCommercialItems(commercialItems: AnalysisTreeModel[], analysis: Analysis): AnalysisTreeModel {
        let unselectedNode = commercialItems.filter(x => x.data.eurodatKey === analysis.eurodatKey).shift();
        if (!unselectedNode) {
            const unselectedSubgroup =
                commercialItems.filter(x => x.children?.filter(q => q.data.eurodatKey === analysis.eurodatKey).length > 0).shift();
            unselectedSubgroup.partialSelected = true;
            const temp = unselectedSubgroup.children.filter(x => x.data.eurodatKey === analysis.eurodatKey).map(q => q.data).shift();
            unselectedNode = { data: temp, parent: unselectedSubgroup };
        }
        return unselectedNode;
    }

    static unselectItemFromSelectedAnalysis(selectedAnalysis: AnalysisTreeModel[], analysis: Analysis): AnalysisTreeModel[] {
        selectedAnalysis = selectedAnalysis.filter(x => x.data.eurodatKey !== analysis.eurodatKey);
        const subGroup = selectedAnalysis.filter(x => x.children?.filter(q => q.data.eurodatKey === analysis.eurodatKey)).shift();
        if (subGroup) {
            selectedAnalysis = selectedAnalysis.filter(x => x !== subGroup);
        }
        return selectedAnalysis;
    }
    //#endregion Analysis Node

    //#region  Favorite Analysis
    static setFavoriteProperty(node: any): boolean {
        const currentValue = node.data.isFavorite;
        node.data.isFavorite = !currentValue;
        if (node.children) {
            node.children.map(x => x.data.isFavorite = !currentValue);
        }
        if (node.parent) {
            const hasFavChild = node.parent.children.filter(x => x.data.isFavorite);
            node.parent.data.isFavorite = hasFavChild.length === node.parent.children.length;
            node.parent.data.isPartialFavorite = false;
            if (!node.parent.data.isFavorite) {
                node.parent.data.isPartialFavorite = hasFavChild.length ? true : false;
            }
        }

        return currentValue;
    }

    static getFavoriteAnalysis(selectedNode: any): Analysis {
        this.setFavoriteProperty(selectedNode.node);
        let analysis: Analysis;
        if (selectedNode.node.parent) {
            analysis = selectedNode.node.parent.data.isFavorite ?
                this.getSubGroupItem(selectedNode.node.parent) :
                this.getIndividualCiItems(selectedNode);
        } else {
            analysis = this.getAnalysisFromNode(selectedNode);
        }
        return analysis;
    }

    static getUnFavoriteAnalysis(selectedNode: any, inputAnalysis: QuotedItem): Analysis[] {
        let unFavAnalysis: Analysis[] = [];
        let favAnalysis: Analysis[] = [];
        if (selectedNode.node.parent) {
            const sgAnalysis =
                this.getSubGroupQuotationLines(inputAnalysis.quotationCommercialItem, selectedNode.node.parent.data.eurodatKey);
            const otherSubGroupItems = sgAnalysis.filter(x => x.eurodatKey !== selectedNode.node.data.eurodatKey);
            const favItemsInSubGroup = selectedNode.node.parent.children.filter(x => x.data.isFavorite).map(y => y.data.eurodatKey);
            favAnalysis = otherSubGroupItems.filter(x => favItemsInSubGroup.indexOf(x.eurodatKey) > -1);
            unFavAnalysis = sgAnalysis.filter(x => x.eurodatKey === selectedNode.node.data.eurodatKey);
        } else {
            unFavAnalysis.push(this.getAnalysisFromNode(selectedNode));
        }
        unFavAnalysis.map(x => x.isFavorite = false);
        if (favAnalysis.length) {
            favAnalysis.map(x => x.isFavorite = true);
            unFavAnalysis.push(...favAnalysis);
        }
        this.setFavoriteProperty(selectedNode.node);
        return unFavAnalysis;
    }

    static getSelectedAnalysis(selectedNode: any, inputAnalysis: QuotedItem, isSelected = false): Analysis[] {
        const analysis = this.getAnalysisFromNode(selectedNode);
        const listAnalysis: Analysis[] = [];
        if (analysis.subGroupKey && !analysis.isCustomerPackage && !analysis.testCode && !analysis.packageCode) {
            listAnalysis.push(...this.getSubGroupQuotationLines
                (inputAnalysis.quotationCommercialItem, analysis.subGroupKey));
        } else {
            listAnalysis.push(analysis);
        }
        return listAnalysis;
    }

    static removeAnalysisFromArray(arr: Array<Analysis>, value: Analysis){ 
        const index = arr.map(e => e.eurodatKey).indexOf(value.eurodatKey);
        if (index > -1) {
          arr.splice(index, 1);
        }
        return arr;
      }

    static setAnalysisProperties(inputAnalysisItems: QuotationCommercialItem, listAnalysis: Analysis[], isSelected = false) {
        listAnalysis.forEach(x => {
            if (x.analysisType === AnalysisType.CustomerPackage) {
                inputAnalysisItems.customerPackages.filter(y => y.code === x.eurodatKey)
                    .map(z => z.isSelected = isSelected);
            } else if (x.analysisType === AnalysisType.FreeQLTest) {
                inputAnalysisItems.freeQuotationLinesTest.filter(y => y.code === x.eurodatKey)
                    .map(z => z.isSelected = isSelected);
            } else if (x.analysisType === AnalysisType.FreeQLPackage) {
                inputAnalysisItems.freeQuotationLinesPackage.filter(y => y.code === x.eurodatKey)
                    .map(z => z.isSelected = isSelected);
            } else if (x.analysisType === AnalysisType.SubgroupWithTest) {
                const subGroup = inputAnalysisItems.commercialItemSubGroups
                    .filter(y => y.code === x.subGroupKey).shift();
                subGroup.testQuotationLines.filter(y => y.code === x.eurodatKey)
                    .map(z => z.isSelected = isSelected);
            } else if (x.analysisType === AnalysisType.SubgroupWithPackage) {
                const subGroup = inputAnalysisItems.commercialItemSubGroups
                    .filter(y => y.code === x.subGroupKey).shift();
                subGroup.packageQuotationLines.filter(y => y.code === x.eurodatKey)
                    .map(z => z.isSelected = isSelected);
            }
        });
    }

    static getStructuredCommentValue(analysis: any, parentAnalysis?: any) {
        if (parentAnalysis && (!parentAnalysis.isSubGroup && parentAnalysis.subgroupCode == null) && parentAnalysis.childDisplay?.length > 0) {
            // it's a package/customer package so don't show the child analysis structured comment
            return '';
        }

        return analysis.structuredComment?.value ?? 'None';
    }

    //#endregion
}
